export function isLocal() {
  return import.meta.env.MODE === 'development';
}

export function isDevelopment() {
  return window.location.hostname.startsWith('dev');
}

export function isDemo() {
  return window.location.hostname.startsWith('demo');
}

export function isStaging() {
  return window.location.hostname.startsWith('staging');
}

const stagingAgentEnvironments = ['star1', 'star2', 'testing', 'debug'];

// 'panda.debug.foresightdata.com' with ['debug'] -> 'foresightdata.com'; undefined if no label
// matches or the matched label is last (an env label with no domain after it is not a valid
// environment hostname), so callers can rely on any returned string being a non-empty domain.
function getDomainAfterEnvironmentLabel(environments: string[]) {
  const labels = window.location.hostname.split('.');
  const index = labels.findIndex((label) => environments.includes(label));
  if (index === -1 || index === labels.length - 1) {
    return undefined;
  }
  return labels.slice(index + 1).join('.');
}

export function isStagingAgentEnvironment() {
  return getDomainAfterEnvironmentLabel(stagingAgentEnvironments) !== undefined;
}

export function isProduction() {
  return !isLocal() && !isDevelopment();
}

export function isNotProduction() {
  return isLocal() || isDevelopment();
}

export function getEnvironment() {
  if (isLocal()) {
    return 'local';
  }

  return window.location.host.split('.')[0];
}

export function getDomain() {
  return window.location.hostname.split('.').slice(1).join('.');
}

export function getWssUrl() {
  if (isLocal()) {
    return getEnvVariable('VITE_FD_LOCAL_WSS_URL') || 'wss://api.dev.foresightdata.com';
  }

  return `wss://api.${getEnvironment()}.${getDomain()}`;
}

export function getApiUrl() {
  if (isLocal()) {
    return getEnvVariable('VITE_FD_LOCAL_API_URL') || 'https://api.dev.foresightdata.com';
  }

  return `https://api.${getEnvironment()}.${getDomain()}`;
}

function getAgentHost() {
  const devDomain = getDomainAfterEnvironmentLabel(['dev']);
  if (devDomain) {
    return `agent.dev.${devDomain}`;
  }
  const demoDomain = getDomainAfterEnvironmentLabel(['demo', 'staging']);
  if (demoDomain) {
    return `agent.demo.${demoDomain}`;
  }
  const stagingAgentDomain = getDomainAfterEnvironmentLabel(stagingAgentEnvironments);
  if (stagingAgentDomain) {
    return `agent.staging.${stagingAgentDomain}`;
  }
  return `agent.${getDomain()}`;
}

export function getAgentApiUrl() {
  if (isLocal()) {
    return getEnvVariable('VITE_FD_LOCAL_AGENT_API_URL') || 'https://agent.dev.foresightdata.com';
  }
  return `https://${getAgentHost()}`;
}

export function getSourcingApiUrl() {
  if (isLocal()) {
    return getEnvVariable('VITE_FD_LOCAL_SOURCING_API_URL') || 'https://sourcing.dev.foresightdata.com';
  }

  return `https://sourcing.${getEnvironment()}.${getDomain()}`;
}

export function getAgentWssUrl() {
  if (isLocal()) {
    return getEnvVariable('VITE_FD_LOCAL_AGENT_WSS_URL') || 'wss://agent.dev.foresightdata.com/ws/chat';
  }
  return `wss://${getAgentHost()}/ws/chat`;
}

export const getEnvVariable = (envKey: keyof ImportMetaEnv): string => {
  return import.meta.env[envKey] ?? '';
};
